/**
 * ReadableText.jsx — Cycle 56 · Part 1
 * ────────────────────────────────────────────────────────────
 * A wrapper that renders text and (when the child's reading level
 * asks for it) speaks it aloud on mount + provides a persistent
 * "read to me" button + sentence-level highlighting.
 *
 * Usage:
 *   <ReadableText text="What is 6 x 7?" kind="question" />
 *   <ReadableText kind="body">
 *     ...markup...  (children mode; the button reads the given `text` fallback)
 *   </ReadableText>
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Volume2, VolumeX, Pause, Play } from "lucide-react";
import { speak, stopSpeaking, pauseSpeaking, resumeSpeaking, userRate } from "@/lib/speech";
import { shouldAutoRead, shouldShowReadButton, recordReadingSignal } from "@/lib/readingLevel";

// Rough sentence splitter — good enough for kid-facing content.
function splitSentences(text) {
  const t = String(text || "").trim();
  if (!t) return [];
  return t.split(/(?<=[.!?])\s+/).filter(Boolean);
}

export default function ReadableText({
  text,
  kind = "body", // "question" | "instruction" | "body" | "menu"
  className = "",
  autoRead = null, // null = derive from level; true|false = force
  showButton = null,
  as = "div",
  children,
  ...rest
}) {
  const As = as;
  const [speaking, setSpeaking] = useState(false);
  const [pausedUI, setPausedUI] = useState(false);
  const [activeSentence, setActive] = useState(-1);
  const timeoutRef = useRef(null);
  const speakContent = String(text || (typeof children === "string" ? children : "")).trim();

  const doSpeak = useCallback(async () => {
    if (!speakContent) return;
    stopSpeaking();
    setSpeaking(true);
    setPausedUI(false);
    setActive(-1);
    recordReadingSignal({ kind: "read_aloud_used", weight: 1 });
    const sentences = splitSentences(speakContent);
    if (sentences.length === 0) { setSpeaking(false); return; }
    // Speak whole text once (single TTS call), and animate sentence
    // highlight over a rough duration heuristic. This is a good-enough
    // proxy for word-level highlighting when TTS timing isn't exposed.
    speak(speakContent, { voice: "nova" });
    const wordsTotal = speakContent.split(/\s+/).length || 1;
    const speedWpm = 140 * userRate(); // highlight timing follows the Accessibility read-aloud speed
    const totalMs = (wordsTotal / speedWpm) * 60 * 1000;
    let acc = 0;
    sentences.forEach((s, idx) => {
      const w = s.split(/\s+/).length || 1;
      const dur = (w / wordsTotal) * totalMs;
      const start = acc;
      acc += dur;
      window.setTimeout(() => setActive(idx), start);
    });
    timeoutRef.current = window.setTimeout(() => {
      setActive(-1);
      setSpeaking(false);
      setPausedUI(false);
    }, acc + 300);
  }, [speakContent]);

  const doStop = useCallback(() => {
    stopSpeaking();
    if (timeoutRef.current) { window.clearTimeout(timeoutRef.current); timeoutRef.current = null; }
    setSpeaking(false);
    setPausedUI(false);
    setActive(-1);
  }, []);
  const togglePause = useCallback(() => {
    if (pausedUI) { resumeSpeaking(); setPausedUI(false); } else { pauseSpeaking(); setPausedUI(true); }
  }, [pausedUI]);

  // Auto-read on mount / content change based on level.
  useEffect(() => {
    const shouldGo = autoRead ?? shouldAutoRead(kind);
    if (shouldGo && speakContent) {
      // Delay a tick so mount transitions don't collide with audio start.
      const t = window.setTimeout(doSpeak, 200);
      return () => { window.clearTimeout(t); doStop(); };
    }
    return doStop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speakContent, kind]);

  const btnVisible = showButton ?? shouldShowReadButton();
  const sentences = splitSentences(speakContent);

  return (
    <As className={`readable-text ${className}`} data-testid={`readable-${kind}`} {...rest}>
      {children ? (
        <>{children}</>
      ) : (
        <span>
          {sentences.length > 1 ? sentences.map((s, i) => (
            <span key={i}
                  data-testid={`readable-sent-${i}`}
                  className={activeSentence === i ? "bg-yellow-200/70 rounded-sm px-0.5 -mx-0.5 transition-colors" : ""}
                  style={{ transition: "background-color 200ms" }}>
              {s}{i < sentences.length - 1 ? " " : ""}
            </span>
          )) : speakContent}
        </span>
      )}
      {btnVisible && speakContent && (
        <button
          type="button"
          onClick={speaking ? doStop : doSpeak}
          data-testid="readable-toggle"
          aria-label={speaking ? "Stop reading" : "Read to me"}
          className="ml-2 inline-flex items-center justify-center h-12 w-12 rounded-full border-2 border-slate-900 bg-yellow-300 shadow-[0_2px_0_0_rgba(15,23,42,1)] align-middle"
        >
          {speaking ? <VolumeX size={18} strokeWidth={3} /> : <Volume2 size={18} strokeWidth={3} />}
        </button>
      )}
      {btnVisible && speakContent && speaking && (
        <button
          type="button"
          onClick={togglePause}
          data-testid="readable-pause"
          aria-label={pausedUI ? "Resume reading" : "Pause reading"}
          aria-pressed={pausedUI}
          className="ml-1 inline-flex items-center justify-center h-12 w-12 rounded-full border-2 border-slate-900 bg-white shadow-[0_2px_0_0_rgba(15,23,42,1)] align-middle"
        >
          {pausedUI ? <Play size={18} strokeWidth={3} /> : <Pause size={18} strokeWidth={3} />}
        </button>
      )}
    </As>
  );
}

/** Tiny inline read-aloud button — for cases where we don't want to
 *  wrap the whole block, just add a button next to a heading. */
export function ReadAloudButton({ text, testid = "read-aloud-btn" }) {
  const [on, setOn] = useState(false);
  const go = () => {
    if (on) { stopSpeaking(); setOn(false); return; }
    setOn(true);
    recordReadingSignal({ kind: "read_aloud_used", weight: 1 });
    speak(text, { voice: "nova" });
    window.setTimeout(() => setOn(false), Math.max(2000, (text?.length || 20) * 60));
  };
  return (
    <button type="button" onClick={go} data-testid={testid} aria-label={on ? "Stop reading" : "Read to me"}
            className="inline-flex items-center justify-center h-12 w-12 rounded-full border-2 border-slate-900 bg-yellow-300 shadow-[0_2px_0_0_rgba(15,23,42,1)]">
      {on ? <VolumeX size={18} strokeWidth={3} /> : <Volume2 size={18} strokeWidth={3} />}
    </button>
  );
}
